import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { omit } from "lodash";

import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import InputFieldText from "Components/Common/InputFieldText";
import DateField from "Components/Common/DatePicker";

import {
  useGetUtilityConsumerDetailByConsumerIdQuery,
  usePutActivateConsumerPowerStatusMutation,
  usePutDisconnectUtilityConsumerPowerStatusMutation,
} from "apis/UtilityConsumer/utilityConsumer";

import { onlyNumberKeys } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const validationSchema = Yup.object({
  activationDate: Yup.date().test(
    "isValidActivationDate",
    "Enter future date",
    (_, context) => {
      const [{ value: { activationDate, status } = {} } = {}] =
        context.from || [];

      if (!activationDate && status) {
        return context.createError({ message: "Enter a valid date" });
      }
      if (activationDate && activationDate.isBefore(moment())) {
        return context.createError({ message: "Enter future date" });
      }
      return true;
    }
  ),
});

const EditPowerStatusModal = ({
  payloadValues: { utilityConsumerId, billCalculationIds },
  isTemporaryDisconnected = false,
  handleClose,
  isOtherUtility = false,
  t,
}) => {
  const { data: { data: consumerData } = {}, isLoading } =
    useGetUtilityConsumerDetailByConsumerIdQuery(
      utilityConsumerId,
      {},
      isOtherUtility
    );

  const [disconnectConsumer, { isLoading: isConsumerDeactivating }] =
    usePutDisconnectUtilityConsumerPowerStatusMutation();

  const [activateConsumer, { isLoading: isConsumerActivating }] =
    usePutActivateConsumerPowerStatusMutation();

  const initialValues = useMemo(
    () => ({
      id: consumerData?.id ?? "",
      consumerNumber: consumerData?.consumerNumber ?? "",
      consumerName: consumerData?.consumerName ?? "",
      tariffName: consumerData?.tariffName ?? "",
      powerRequirement: consumerData?.connectionLoad?.powerRequirement ?? "",
      dateOfConnection: consumerData?.dateOfConnection
        ? convertUTCDateToLocalDate(consumerData.dateOfConnection)
        : "",
      disconnectionDate: consumerData?.disconnectionDate
        ? convertUTCDateToLocalDate(consumerData.disconnectionDate)
        : "",

      activationDate:
        consumerData?.activationDate || consumerData?.temporaryDisconnectionDate
          ? convertUTCDateToLocalDate(
              isTemporaryDisconnected
                ? consumerData.activationDate ?? ""
                : consumerData.temporaryDisconnectionDate ?? ""
            )
          : "",
      status: isTemporaryDisconnected
        ? !!consumerData?.activationDate
        : !!consumerData?.temporaryDisconnectionDate,
    }),
    [consumerData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async ({ status, activationDate }, actions) => {
      const payload = {
        billingCalculationId: billCalculationIds,
        isTemporarilyDisconnected: isTemporaryDisconnected ? !status : status,
        [isTemporaryDisconnected
          ? "activationDate"
          : "temporaryDisconnectionDate"]: activationDate
          ? activationDate.format("YYYY-MM-DD")
          : "",
      };
      isTemporaryDisconnected
        ? await activateConsumer(
            omit(
              payload,
              !activationDate
                ? ["activationDate", "temporaryDisconnectionDate"]
                : ""
            )
          )
        : await disconnectConsumer(
            omit(
              payload,
              !activationDate
                ? ["activationDate", "temporaryDisconnectionDate"]
                : ""
            )
          );
      actions.setTouched({});
      handleClose();
    },
  });

  const {
    values: { status },
    handleSubmit,
    setFieldValue,
  } = formik;

  if (isLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Edit Consumer Power Status"
      maxWidth="md"
    >
      <Grid container columnSpacing={1} className="mt-2">
        <Grid item xs={12}>
          <Typography className="mb-3" lineHeight={1} variant="h6">
            {t("Consumer Details")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputFieldText
            label={t("Consumer Number") + "*"}
            name="consumerNumber"
            formik={formik}
            type="number"
            disabled
            InputProps={{
              inputProps: { min: 0, onKeyDown: (e) => onlyNumberKeys(e) },
            }}
            placeholder="Enter Consumer Number"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputFieldText
            label={t("Consumer Name") + "*"}
            name="consumerName"
            formik={formik}
            disabled
            placeholder="Enter Consumer Name"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DateField
            label={t("Connection Date")}
            name="dateOfConnection"
            formik={formik}
            disabled
            format="DD-MM-YYYY"
            placeholder="Select Connection Date"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DateField
            label={t("Disconnection Date")}
            name="disconnectionDate"
            formik={formik}
            disabled
            format="DD-MM-YYYY"
            placeholder="Select Disconnection Date"
            disablePast
          />
        </Grid>
        <Grid item xs={12}>
          <Typography className="mb-3" lineHeight={1} variant="h6">
            {t("Power Status")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={status}
                onChange={() => {
                  setFieldValue("status", !status);
                  setFieldValue("activationDate", "");
                }}
              />
            }
            label={t(
              isTemporaryDisconnected ? "Activate" : "Temporary Disconnect"
            )}
          />
        </Grid>
        {status && (
          <Grid item xs={12} sm={4}>
            <DateField
              label={
                isTemporaryDisconnected
                  ? t("Activation Date") + "*"
                  : t("Disconnection Date") + "*"
              }
              name="activationDate"
              formik={formik}
              format="DD-MM-YYYY"
              placeholder="Select Disconnection Date"
              minDate={moment().add(1, "days")}
            />
          </Grid>
        )}
      </Grid>

      <Button
        className="d-block ms-auto"
        color="primary"
        variant="contained"
        disabled={isConsumerDeactivating || isConsumerActivating}
        onClick={handleSubmit}
      >
        {t("Submit")}{" "}
        {(isConsumerDeactivating || isConsumerActivating) && (
          <CircularProgress size={20} />
        )}
      </Button>
    </Modal>
  );
};

EditPowerStatusModal.propTypes = {
  payloadValues: PropTypes.object,
  handleClose: PropTypes.func,
  isTemporaryDisconnected: PropTypes.bool,
  isOtherUtility: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(EditPowerStatusModal);
